import { Injectable } from '@angular/core';
import { MessageService } from '../service-ui/message.service';
import { HttpClient } from '@angular/common/http';
import { StartupService } from '../service-ui/startup.service';
import {AbstractLiveDataService} from "./abstract-live-data.service";
import {NotificationsService} from "./notifications.service";
import {Draft} from "../../model/Draft";
import {DraftBody} from "../../model/DraftBody";
import {DraftCover} from "../../model/DraftCover";
import {Observable, of} from "rxjs";
import {environment} from "../../environments/environment";
import {catchError, map} from "rxjs/operators";

@Injectable()
export class DraftService extends AbstractLiveDataService<Draft> {

  constructor(private httpa: HttpClient, private messageServicea: MessageService, private startupServicea: StartupService, protected notificationsService: NotificationsService) {
    super(httpa, messageServicea, startupServicea, notificationsService);
    this.notificationsService.setDraftService(this);
    this.notificationsService.startNotification();
  }

  makeObjectInstance(args: any): Draft {
    return new Draft(args);
  }

  getLoggingObjectTypeName (): string {
    return "Draft";
  }

  supportsParallel(): boolean {
    return false;
  }

  getURLEndPoint (): string {
    return "draft";
  }

  getLinkData(): any[] {
    return [
      { action: 'Link', objectIdPropertyName: 'work_ID', serviceName: 'projectService', arrayProperty: 'drafts', objectProperty: 'project'},
      { action: 'Link', objectIdPropertyName: 'body_ID', serviceName: 'draftBodyService', arrayProperty: 'drafts', objectProperty: 'draftBody'},
      { action: 'Link', objectIdPropertyName: 'cover_ID', serviceName: 'draftCoverService', arrayProperty: 'drafts', objectProperty: 'draftCover'},
      // { action: 'Link', objectIdPropertyName: 'book_ID', serviceName: 'bookService', arrayProperty: 'products', objectProperty: 'book'},
      // { action: 'AddUpdateArray', serviceName: 'facilityInventoryProductService', arrayProperty: 'facilityInventoryProducts'},
    ];
  }

  // copy
  public copy(draftId: number): Observable<string> {
    const action = 'copy';
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/${action}/${draftId}`;
    return this.http.put(url, this.startupService.getHttpOptions()).pipe(map(response => {
      return response;
    }), catchError(this.handleErrorX<any>(action)));
  }

  /**
   * Create a brand-new, empty Draft inside an existing Project.
   *
   * There is no server endpoint for this. `POST /project/createproject`
   * with `seedEmptyDraft: true` seeds a Draft, but only into a Project
   * it is creating at the same time, so it cannot serve a Project that
   * already exists — which is exactly the case where the UI had no way
   * to add a Draft at all.
   *
   * What makes this work without a new endpoint is that `POST /draft`
   * routes to `Drafts.createAndNotify(...)`, and the Draft entity
   * declares `{ cascade: true }` on both the `draftBody` and
   * `draftCover` relations. Sending the two sides NESTED in the payload
   * therefore inserts all three rows in one call — the same mechanism
   * the server's own seed path uses.
   *
   * That nesting is not optional. `POST /draftbody` and
   * `POST /draftcover` are NOT registered server-side (only GET / PUT /
   * DELETE are), so the two sides cannot be created separately; and a
   * Draft with no body / cover is useless anyway, because the PDF
   * upload path writes through `draftBody` / `draftCover` objects that
   * would not exist.
   *
   * The literal values below are transcribed from the server's seed
   * block in `Projects.createProject` so a Draft created here is
   * indistinguishable from a seeded one. Note they are the SERVER's
   * defaults, which differ from the client model's own field defaults
   * (36pt margins, not 32) — the server is the one that matters.
   *
   * `org_ID` must match the acting role: `Drafts.add` accepts the call
   * only for `role.type === 'organization'` with `draft.org_ID ===
   * role.id`, and rejects everything else by returning an empty row
   * rather than an error status. Callers MUST therefore check that the
   * emitted Draft has an `ID` before reporting success.
   */
  public createDraftInProject(orgId: number, projectId: number, title: string): Observable<Draft> {
    const draftBody = new DraftBody({
      height: 9 * 72,
      width: 6 * 72,
      spine: 0,
      correctBlackText: 0,
      documentScale: 1,
      leftHorizontalShift: 0,
      rightHorizontalShift: 0,
      leftVerticalShift: 0,
      rightVerticalShift: 0,
      marginTop: 36,
      marginBottom: 36,
      marginIn: 36,
      marginOut: 36,
      numPages: 0,
      paperType_ID: 1,
      useICC: 'FALSE'
    });
    // Explicit null, not undefined: `undefined` is dropped by
    // JSON.stringify, and the column has to be written as NULL for the
    // "no PDF uploaded yet" state to be represented at all.
    (draftBody as any).resource_ID = null;

    const draftCover = new DraftCover({
      documentScale: 1,
      horizontalShift: 0,
      verticalShift: 0,
      paperType_ID: 3,
      laminateType: 'GLOSS',
      isColorBoosted: 'FALSE',
      barcodeSide: 'NONE',
      barcodeOrientationHorz: 'LEFT',
      barcodeOrientationVert: 'BOTTOM',
      barcodePaddingHorz: 0,
      barcodePaddingVert: 0,
      barcodePositionHorz: 36,
      barcodePositionVert: 36
    });
    (draftCover as any).resource_ID = null;

    const draft = new Draft({
      work_ID: projectId,
      org_ID: orgId,
      title: title || '',
      subtitle: '',
      author: '',
      comment: '',
      bindingType: 'PERFECT',
      interiorType: 'GRAY',
      languageCode: '',   // NOT NULL DEFAULT '' server-side
      weight: 0,
      thickness: 0
    });
    // Assigned after construction: `Draft.update()` copies scalars only,
    // so object-valued properties passed to the constructor are dropped.
    draft.draftBody = draftBody;
    draft.draftCover = draftCover;

    return this.addItem(draft);
  }

  // reassignProject
  public reassignProject(draftId: number, newProjectId: number): Observable<string> {
    const action = 'reassignProject';
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/${action}/${draftId}/${newProjectId}`;
    return this.http.put(url, this.startupService.getHttpOptions()).pipe(map(response => {
      return response;
    }), catchError(this.handleErrorX<any>(action)));
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleErrorX<T> (operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      console.error(error, operation); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

}
